// validationSchemas.js
import * as Yup from "yup";
import {MANDATORY_QUES, PERCENTAGE_EXCEED_ERR} from "./constants";


export const optionalNumberSchema = Yup.string()
  .transform((value) =>
    value === null || value === "null" ? "" : value
  )
  .matches(/^\d+$/, {
    message: "Please enter a valid number!",
    excludeEmptyString: true,
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (!value) return true;
    return Number(value) >= 0;
  })
  .notRequired();

// Reusable number validation schema
export const numberSchema = Yup.string()
  .matches(/^\d+$/, "Please enter a valid number!") // Only allow digits
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value === undefined || value === "") return false; // Handle empty case explicitly
    const num = Number(value);
    return !isNaN(num) && Number.isInteger(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (value === undefined || value === "") return false; // Handle empty case for min
    return Number(value) >= 0;
  })
  .required(MANDATORY_QUES); // Ensures the field is required

// Cheque Number Schema: Exactly 6 digits
export const chequeNumberSchema = Yup.string()
  .matches(/^\d{6}$/, "Cheque number must be exactly 6 digits")
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value === undefined || value === "") return false; // Handle empty case explicitly
    const num = Number(value);
    return !isNaN(num) && Number.isInteger(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (value === undefined || value === "") return false; // Handle empty case for min
    return Number(value) >= 0;
  })
  .required("Cheque number is required");

// Card Number Schema: Between 13 and 19 digits
export const cardNumberSchema = Yup.string()
  .matches(/^\d{13,19}$/, "Card number must be between 13 and 19 digits")
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value === undefined || value === "") return false; // Handle empty case explicitly
    const num = Number(value);
    return !isNaN(num) && Number.isInteger(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (value === undefined || value === "") return false; // Handle empty case for min
    return Number(value) >= 0;
  })
  .required("Card number is required");

export const amountSchemaWithRequiredOLD = Yup.string()
  .required(MANDATORY_QUES) // ✅ Enforce required validation
  .test("is-empty", "", (value) => {
    // Allow empty string or null, but required will catch empty values
    if (value === "" || value === null || value === undefined) {
      return false; // Will trigger the required error
    }
    return true;
  })
  // Match US currency format (e.g., $1,000.00 or 1000)
  .matches(
    /^(\$)?(\d{1,3}(,\d{3})*|(\d+))(\.\d{1,2})?$/,
    "Please enter a valid amount in USD format!"
  )
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value === undefined || value === "" || value === null) return false;

    // Remove commas and dollar sign before validating
    const num = Number(value.replace(/[$,]/g, ""));

    return !isNaN(num) && Number.isFinite(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (value === undefined || value === "" || value === null) return false;

    const num = Number(value.replace(/[$,]/g, ""));
    return num >= 0;
  })
  .test("max-digits", "Amount can't exceed 10 digits!", (value) => {
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === "0" ||
      value === "$0"
    ) {
      return true; // Allow empty or zero values (let required or min handle these if needed)
    }

    const cleaned = value.replace(/[$,]/g, "");
    const [integerPart] = cleaned.split(".");

    return integerPart.length <= 10;
  });

export const amountSchemaWithRequiredGreaterThanZeroOLD = Yup.string()
  .required(MANDATORY_QUES)
  .test("is-empty", "", (value) => {
    if (value === "" || value === null || value === undefined) {
      return false;
    }
    return true;
  })
  .matches(
    /^(\$)?(\d{1,3}(,\d{3})*|(\d+))(\.\d{1,2})?$/,
    "Please enter a valid amount in USD format!"
  )
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value === undefined || value === "" || value === null) return false;
    const num = Number(value.replace(/[$,]/g, ""));
    return !isNaN(num) && Number.isFinite(num);
  })
  .test("greater-than-zero", "Amount must be greater than $0", (value) => {
    if (value === undefined || value === "" || value === null) return false;
    const num = Number(value.replace(/[$,]/g, ""));
    return num > 0;
  })
  .test("max-digits", "Amount can't exceed 10 digits!", (value) => {
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === "0" ||
      value === "$0"
    ) {
      return true; // Let "greater-than-zero" handle zero
    }

    const cleaned = value.replace(/[$,]/g, "");
    const [integerPart] = cleaned.split(".");
    return integerPart.length <= 10;
  });

export const amountSchemaWithRequiredWithoutCharacterCountlimit = Yup.string()
  .required(MANDATORY_QUES) // ✅ Enforce required validation
  .test("is-empty", "", (value) => {
    // Allow empty string or null, but required will catch empty values
    if (value === "" || value === null || value === undefined) {
      return false; // Will trigger the required error
    }
    return true;
  })
  // Match US currency format (e.g., $1,000.00 or 1000)
  .matches(
    /^(\$)?(\d{1,3}(,\d{3})*|(\d+))(\.\d{1,2})?$/,
    "Please enter a valid amount in USD format!"
  )
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value === undefined || value === "" || value === null) return false;

    // Remove commas and dollar sign before validating
    const num = Number(value.replace(/[$,]/g, ""));

    return !isNaN(num) && Number.isFinite(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (value === undefined || value === "" || value === null) return false;

    const num = Number(value.replace(/[$,]/g, ""));
    return num >= 0;
  })
  .test("max-digits", "Amount can't exceed 20 digits!", (value) => {
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === "0" ||
      value === "$0"
    ) {
      return true; // Allow empty or zero values (let required or min handle these if needed)
    }

    const cleaned = value.replace(/[$,]/g, "");
    const [integerPart] = cleaned.split(".");

    return integerPart.length <= 20;
  });

export const numberSchemaWithoutRequiredOLD = Yup.string()
  .matches(/^\d+$/, "Please enter a valid number!") // Only allow digits
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value === undefined || value === "") return false; // Handle empty case explicitly
    const num = Number(value);
    return !isNaN(num) && Number.isInteger(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (value === undefined || value === "") return false; // Handle empty case for min
    return Number(value) >= 0;
  });

export const numberSchemaWithoutRequired = Yup.string()
  .nullable()
  .test("is-empty", "", (value) => {
    // Allow empty string or null
    if (value === "" || value === null || value === undefined) {
      return true;
    }
    return true; // Skip validation if empty or null
  })
  .matches(/^\d+$/, "Please enter a valid number!") // Only allow digits
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value === undefined || value === "" || value === null) return true;
    const num = Number(value);
    return !isNaN(num) && Number.isInteger(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (value === undefined || value === "" || value === null) return true;
    return Number(value) >= 0;
  });

export const amountSchemaWithoutRequiredOLD = Yup.string()
  .nullable()
  .test("is-empty", "", (value) => {
    // Allow empty string or null
    if (value === "" || value === null || value === undefined) {
      return true;
    }
    return true;
  })
  // Match US currency format (e.g., $1,000.00 or 1000)
  .matches(
    /^(\$)?(\d{1,3}(,\d{3})*|(\d+))(\.\d{1,2})?$/,
    "Please enter a valid amount in USD format!"
  )
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value === undefined || value === "" || value === null) return true;

    // Remove commas and dollar sign before validating
    const num = Number(value.replace(/[$,]/g, ""));

    return !isNaN(num) && Number.isFinite(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (value === undefined || value === "" || value === null) return true;

    const num = Number(value.replace(/[$,]/g, ""));
    return num >= 0;
  })
  .test("max-digits", "Amount can't exceed 10 digits!", (value) => {
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === "0" ||
      value === "$0"
    ) {
      return true; // Allow empty or zero values (let required or min handle these if needed)
    }

    const cleaned = value.replace(/[$,]/g, "");
    const [integerPart] = cleaned.split(".");

    return integerPart.length <= 10;
  });

  export const optionalNumberSchemaWithPercentage = Yup.string()
  .matches(/^\d+$/, {
    message: "Please enter a valid number!",
    excludeEmptyString: true,
  })
  .test("is-number", "Please enter a valid number!", (value) => {
    if (!value) return true;
    const num = Number(value);
    return !isNaN(num) && Number.isInteger(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (!value) return true;
    return Number(value) >= 0;
  })
  .test("max", PERCENTAGE_EXCEED_ERR, (value) => {
    if (!value) return true;
    return Number(value) <= 100;
  })
  .notRequired();

export const numberSchemaWithPercentage = Yup.string()
  .matches(/^\d+$/, "Please enter a valid number!") // Only allow digits
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value == undefined || value == "") return false; // Handle empty case explicitly
    const num = Number(value);
    return !isNaN(num) && Number.isInteger(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (value == undefined || value == "") return false; // Handle empty case for min
    return Number(value) >= 0;
  })
  .test("max", PERCENTAGE_EXCEED_ERR, (value) => {
    if (value == undefined || value == "") return false; // Handle empty case for max
    return Number(value) <= 100;
  })
  .required(MANDATORY_QUES);

export const numberSchemaWithPercentageWithoutRequired = Yup.string()
  .matches(/^\d+$/, "Please enter a valid number!") // Only allow digits
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value === undefined || value === "") return false; // Handle empty case explicitly
    const num = Number(value);
    return !isNaN(num) && Number.isInteger(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (value === undefined || value === "") return false; // Handle empty case for min
    return Number(value) >= 0;
  })
  .test("max", PERCENTAGE_EXCEED_ERR, (value) => {
    if (value === undefined || value === "") return false; // Handle empty case for max
    return Number(value) <= 100;
  });

export const numberSchemaWithPercentageWithDecimal = Yup.string()
  .matches(/^\d*\.?\d+$/, "Please enter a valid number!") // Allow digits with optional decimal
  .test("is-number", "Please enter a valid number!", (value) => {
    if (value == undefined || value === "") return false;
    const num = Number(value);
    return !isNaN(num); // Can be integer or decimal
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (value == undefined || value === "") return false;
    return Number(value) >= 0;
  })
  .test("max", PERCENTAGE_EXCEED_ERR, (value) => {
    if (value == undefined || value === "") return false;
    return Number(value) <= 100;
  })
  .required(MANDATORY_QUES);


  export const optionalAmountSchema = Yup.string()
  .matches(
    /^\$[0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]{1,2})?$|^\$[0-9]+(?:\.[0-9]{1,2})?$/,
    {
      message: "Please enter a valid USD amount (e.g. $1,000.50)",
      excludeEmptyString: true,
    }
  )
  .test("is-number", "Please enter a valid number!", (value) => {
    if (!value) return true;
    const num = Number(value.replace(/[$,]/g, ""));
    return !isNaN(num) && Number.isFinite(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (!value) return true;
    const num = Number(value.replace(/[$,]/g, ""));
    return num >= 0;
  })
  .test(
    "max-digits",
    "Amount can't exceed 10 digits before decimal!",
    (value) => {
      if (!value) return true;
      const cleaned = value.replace(/[$,]/g, "");
      const [integerPart] = cleaned.split(".");
      return integerPart.length <= 10;
    }
  )
  .notRequired();
//NEW
export const amountSchemaWithRequired = Yup.string()
  .required(MANDATORY_QUES)
  // ✅ Matches valid USD formats like $1,000, $1,000.5, $1,000.50
  .matches(
    /^\$[0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]{1,2})?$|^\$[0-9]+(?:\.[0-9]{1,2})?$/,
    "Please enter a valid USD amount (e.g. $1,000.50)"
  )
  .test("is-number", "Please enter a valid number!", (value) => {
    if (!value) return false;
    const num = Number(value.replace(/[$,]/g, ""));
    return !isNaN(num) && Number.isFinite(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (!value) return false;
    const num = Number(value.replace(/[$,]/g, ""));
    return num >= 0;
  })
  .test(
    "max-digits",
    "Amount can't exceed 10 digits before decimal!",
    (value) => {
      if (!value) return true;
      const cleaned = value.replace(/[$,]/g, "");
      const [integerPart] = cleaned.split(".");
      return integerPart.length <= 10;
    }
  );

export const amountSchemaWithoutRequired = Yup.string()
  .nullable()
  .test("is-empty", "", (value) => {
    // Allow empty string, null, or undefined
    return value === "" || value === null || value === undefined ? true : true;
  })
  // ✅ Allow $ optional, commas, and up to 2 decimal places
  .matches(
    /^(\$)?(\d{1,3}(,\d{3})*|\d+)(\.\d{1,2})?$/,
    "Please enter a valid amount (e.g. $1,000.50)"
  )
  .test("is-number", "Please enter a valid number!", (value) => {
    if (!value) return true;
    const num = Number(value.replace(/[$,]/g, ""));
    return !isNaN(num) && Number.isFinite(num);
  })
  .test("min", "Amount can't be less than 0", (value) => {
    if (!value) return true;
    const num = Number(value.replace(/[$,]/g, ""));
    return num >= 0;
  })
  .test(
    "max-digits",
    "Amount can't exceed 10 digits before decimal!",
    (value) => {
      if (!value) return true;
      const cleaned = value.replace(/[$,]/g, "");
      const [integerPart] = cleaned.split(".");
      return integerPart.length <= 10;
    }
  );

export const amountSchemaWithRequiredGreaterThanZero = Yup.string()
  .required(MANDATORY_QUES)
  .matches(
    /^(\$)?(\d{1,3}(,\d{3})*|\d+)(\.\d{1,2})?$/,
    "Please enter a valid amount (e.g. $1,000.50 or 1000.25)"
  )
  .test("is-number", "Please enter a valid number!", (value) => {
    if (!value) return false;
    const num = Number(value.replace(/[$,]/g, ""));
    return !isNaN(num) && Number.isFinite(num);
  })
  .test("greater-than-zero", "Amount must be greater than $0", (value) => {
    if (!value) return false;
    const num = Number(value.replace(/[$,]/g, ""));
    return num > 0;
  })
  .test(
    "max-digits",
    "Amount can't exceed 10 digits before decimal!",
    (value) => {
      if (!value) return true;
      const cleaned = value.replace(/[$,]/g, "");
      const [integerPart] = cleaned.split(".");
      return integerPart.length <= 10;
    }
  );
